import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertOctagon, ChevronLeft, Check, ShieldAlert, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleLIFT } from "@/components/lift/ModuleTitleLIFT";

export default function RedactionQueue() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeRow, setActiveRow] = useState(null);
  const [editedS, setEditedS] = useState("");
  const [editedG, setEditedG] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const [bg, setBg] = useState(null); // active break-glass target
  const [bgJust, setBgJust] = useState("");
  const [bgResult, setBgResult] = useState(null);
  const [bgBusy, setBgBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lift/redaction-queue");
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openRow = (r) => {
    setActiveRow(r);
    setEditedS(r.edited_strengths_text || r.strengths_text || "");
    setEditedG(r.edited_growth_text || r.growth_text || "");
    setNotes("");
  };

  const submit = async (action) => {
    if (!activeRow) return;
    setBusy(true);
    try {
      await api.post(`/lift/redaction/${activeRow.id}`, {
        edited_strengths_text: editedS,
        edited_growth_text: editedG,
        action,
        notes,
      });
      toast.success(action === "approve" ? "Approved for release." : "Withheld.");
      setActiveRow(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doBreakGlass = async () => {
    if (bgJust.trim().length < 20) {
      toast.error("Justification must be at least 20 characters.");
      return;
    }
    setBgBusy(true);
    try {
      const { data } = await api.post(`/lift/break-glass/${bg.id}`, { justification: bgJust });
      setBgResult(data.reviewer);
      toast.success("Reviewer identity revealed — this is audit-logged.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBgBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="lift-redaction-queue">
      <Link to="/lift" className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> LIFT home
      </Link>
      <div>
        <ModuleTitleLIFT />
        <h1 className="mt-3 text-4xl font-serif text-forest-900">Redaction Queue</h1>
        <p className="mt-2 max-w-2xl text-charcoal-soft">
          Review peer feedback free-text before it is shared anonymously with the employee.
          Edit to redact identifying phrases, approve, or withhold. Original text is preserved in version history.
        </p>
      </div>

      {loading ? (
        <p className="italic text-charcoal-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="patria-card p-10 text-center">
          <Check className="h-8 w-8 mx-auto text-forest-600" strokeWidth={1.5} />
          <p className="mt-3 text-charcoal-soft">The queue is empty.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.id} className="patria-card p-5" data-testid={`redaction-row-${r.id}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-forest-900">
                    Feedback for <span className="font-serif">{r.subject_name}</span>
                  </p>
                  <p className="text-xs text-charcoal-muted">{r.cycle_name} · submitted {r.submitted_at}</p>
                </div>
                <button className="patria-btn-primary" onClick={() => openRow(r)} data-testid={`redaction-open-${r.id}`}>
                  Review
                </button>
                <button
                  className="patria-btn-terracotta"
                  onClick={() => { setBg(r); setBgJust(""); setBgResult(null); }}
                  data-testid={`redaction-breakglass-${r.id}`}
                >
                  <ShieldAlert className="h-4 w-4" strokeWidth={1.5} /> Break-Glass
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Review drawer */}
      {activeRow && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-forest-900/40" onClick={() => setActiveRow(null)} />
          <div className="relative ml-auto h-full w-full max-w-2xl bg-cream-100 overflow-y-auto p-6 shadow-card">
            <h3 className="text-2xl font-serif text-forest-900">Review Peer Feedback</h3>
            <p className="text-xs text-charcoal-muted">For {activeRow.subject_name}</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ReadonlyBlock title="Original — strengths" value={activeRow.strengths_text} />
              <div>
                <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">
                  Edited — strengths (shown to employee)
                </label>
                <textarea className="patria-input w-full min-h-[140px]" value={editedS} onChange={(e) => setEditedS(e.target.value)} data-testid="redact-strengths-edit" />
              </div>
              <ReadonlyBlock title="Original — growth" value={activeRow.growth_text} />
              <div>
                <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">
                  Edited — growth (shown to employee)
                </label>
                <textarea className="patria-input w-full min-h-[140px]" value={editedG} onChange={(e) => setEditedG(e.target.value)} data-testid="redact-growth-edit" />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">Notes (Audit)</label>
              <input className="patria-input w-full" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button className="patria-btn-secondary" onClick={() => setActiveRow(null)}>Cancel</button>
              <button className="patria-btn-terracotta" onClick={() => submit("withhold")} disabled={busy} data-testid="redact-withhold">
                <X className="h-4 w-4" strokeWidth={1.5} /> Withhold
              </button>
              <button className="patria-btn-primary" onClick={() => submit("approve")} disabled={busy} data-testid="redact-approve">
                <Check className="h-4 w-4" strokeWidth={1.5} /> Approve for release
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Break-glass solemn modal */}
      {bg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/50" data-testid="break-glass-modal">
          <div className="patria-card w-full max-w-lg p-6 border-2 border-terracotta-500/60">
            <div className="flex items-start gap-3">
              <AlertOctagon className="h-6 w-6 text-terracotta-500 mt-1" strokeWidth={1.5} />
              <div>
                <h3 className="text-2xl font-serif text-forest-900">Break-Glass De-Anonymization</h3>
                <p className="mt-2 text-sm text-charcoal-soft">
                  Revealing a reviewer&rsquo;s identity is a serious action, taken only when there is a policy or safety
                  concern. Your justification will be permanently recorded in the audit log and highlighted to Super Admins.
                </p>
              </div>
            </div>
            {!bgResult ? (
              <>
                <label className="block mt-4 text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">
                  Justification (min 20 characters)
                </label>
                <textarea
                  className="patria-input w-full min-h-[110px]"
                  value={bgJust}
                  onChange={(e) => setBgJust(e.target.value)}
                  data-testid="break-glass-justification"
                />
                <div className="mt-4 flex justify-end gap-3">
                  <button className="patria-btn-secondary" onClick={() => setBg(null)}>Cancel</button>
                  <button className="patria-btn-terracotta" onClick={doBreakGlass} disabled={bgBusy} data-testid="break-glass-confirm">
                    {bgBusy ? "Recording..." : "Reveal & audit"}
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-4 rounded-xl border border-terracotta-500/40 bg-terracotta-500/5 p-4">
                <p className="text-xs uppercase tracking-widest text-terracotta-600 font-semibold">Reviewer Identity</p>
                <p className="mt-1 font-medium text-forest-900">{bgResult.name || "(unknown)"}</p>
                <p className="text-sm text-charcoal-soft">{bgResult.email || "—"}</p>
                <p className="mt-3 text-xs text-charcoal-muted italic">
                  This action has been written to the audit log with your justification.
                </p>
                <div className="mt-4 flex justify-end">
                  <button className="patria-btn-primary" onClick={() => setBg(null)}>Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ReadonlyBlock({ title, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">{title}</p>
      <div className="rounded-lg bg-sage-100 border border-sage-300/60 p-3 text-sm whitespace-pre-wrap text-charcoal min-h-[140px]">
        {value || <span className="italic text-charcoal-muted">(empty)</span>}
      </div>
    </div>
  );
}
